import { useCallback } from 'react';

import * as env from 'src/lib/env';
import { useNotificationActions } from 'src/store';

const ADOBE_EXPRESS_Z_INDEX = 10_000; // Needs to appear above all other elements
const ADOBE_EXPRESS_INITIALIZATION_ERROR = new Error('Failed to initialize Adobe Express SDK');
const ADOBE_EXPRESS_SRC = 'https://cc-embed.adobe.com/sdk/v4/CCEverywhere.js';
const BASE_64_PREFIX = 'data:image/jpeg;base64,';

const globalSDKState = {
  initialized: false,
  editor: null,
};

export const useAdobeExpressSDK = (onSave) => {
  const notificationActions = useNotificationActions();

  const launchEditorWithAsset = useCallback(
    (result, editor) => {
      const docConfig = {
        asset: {
          data: result,
          dataType: 'base64',
          type: 'image',
        },
      };

      const callbacks = {
        onCancel: () => {},
        onPublish: (_, data) => {
          onSave(data);
        },
        onError: () => {},
      };

      const appConfig = {
        callbacks: callbacks,
      };

      const containerConfig = {
        zIndex: ADOBE_EXPRESS_Z_INDEX,
      };

      editor.createWithAsset(docConfig, appConfig, undefined, containerConfig);
    },
    [onSave]
  );

  const initializeAdobeSDK = useCallback(
    async (result) => {
      if (globalSDKState.initialized) {
        launchEditorWithAsset(result, globalSDKState.editor);
        return;
      }

      try {
        const { editor } = await window.CCEverywhere.initialize({
          clientId: env.getAdobeExpressClientId(),
          appName: 'AI Writing Platform',
        });

        globalSDKState.initialized = true;
        globalSDKState.editor = editor;
        launchEditorWithAsset(result, editor);
      } catch {
        notificationActions.displayError(ADOBE_EXPRESS_INITIALIZATION_ERROR);
      }
    },
    [launchEditorWithAsset, notificationActions]
  );

  const loadAdobeSDK = useCallback(
    (imageBlob) => {
      const formattedImageBlob = `${BASE_64_PREFIX}${imageBlob}`;

      if (globalSDKState.initialized) {
        launchEditorWithAsset(formattedImageBlob, globalSDKState.editor);
        return;
      }

      const script = document.createElement('script');
      script.src = ADOBE_EXPRESS_SRC;
      script.async = true;
      script.onload = () => initializeAdobeSDK(formattedImageBlob);
      document.body.appendChild(script);
    },
    [initializeAdobeSDK, launchEditorWithAsset]
  );

  return { loadAdobeSDK };
};
